<script setup lang="ts">
import type { Swiper as SwiperInstance, NavigationOptions } from 'swiper/types'
import { useJackPot } from '~/composables/common/use-jackpot';
import { useSwiper } from '~/composables/common/use-swiper'
import { useGames } from '~/composables/games/use-games';
import type { GameItem } from '~/composables/lobby-games/interface';
import { LOBBY_GAME_URLS } from '~/configs/page-url';
import AnimatedNumber from '~/components/common/animated-number.vue';

const { jackpots, allGameList, getGameTopWinners, topWinners } = useJackPot()
const { playGame } = useGames()
const { FLAT_CURRENCY_UNIT = '' } = useRuntimeConfig().public
const { modules } = useSwiper()
const jackpotSwiper = ref<SwiperInstance | null>(null)
const jackpotNavPrevEl = ref<HTMLElement | null>(null)
const jackpotNavNextEl = ref<HTMLElement | null>(null)

function onJackpotSwiperInit(swiper: SwiperInstance) {
  jackpotSwiper.value = swiper
  if (jackpotNavPrevEl.value && jackpotNavNextEl.value) {
    const existingNav = swiper.params.navigation
    const navParams: NavigationOptions =
      (typeof existingNav === 'boolean' ? ({} as NavigationOptions) : (existingNav as NavigationOptions))
    navParams.prevEl = jackpotNavPrevEl.value
    navParams.nextEl = jackpotNavNextEl.value
    swiper.params.navigation = navParams
    swiper.navigation.init()
    swiper.navigation.update()
  }
}

const topJackpots = computed(() => {
  const sortedJackpots = Object.entries(jackpots.value).sort((a, b) => b[1] - a[1])
  return sortedJackpots.map(([key, value]) => {
    const game = allGameList.value.find((game: GameItem) => game.gameId === key)
    return {
      value,
      game
    }
  }).filter(x => x.game).slice(0, 10)
  // return sortedJackpots.slice(0, 10).map(([key, value]) => {
  //   const game = allGameList.value.find((game: GameItem) => game.gameId === key)
  //   return {
  //     value,
  //     game
  //   }
  // })
})

const totalJackpot = computed(() => {
  return Object.values(jackpots.value).reduce((sum, item) => {
    if (!item || isNaN(item)) {
      return sum
    }
    return sum + item
  }, 0)
})

useAsyncData('jackpot-top-winners', async () => await getGameTopWinners())
</script>

<template>
  <section class="games-hot-section">
    <div class="section-header">
      <h2 class="section-title">HŨ LỚN SẮP NỔ</h2>
      <div class="section-actions">
        <NuxtLink :to="LOBBY_GAME_URLS.NO_HU" class="button-link">
          Xem tất cả
        </NuxtLink>
        <div class="jackpot-slider-navigation">
          <button
            ref="jackpotNavPrevEl"
            type="button"
            class="swiper-nav jackpot-nav-prev"
            aria-label="Previous"
          >
            <i class="icon-chevron-left"></i>
          </button>
          <button
            ref="jackpotNavNextEl"
            type="button"
            class="swiper-nav jackpot-nav-next"
            aria-label="Next"
          >
            <i class="icon-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="section-wrapper">
      <div class="jackpot-slider">
        <div class="jackpot-number">
          <AnimatedNumber :number="totalJackpot" :start="totalJackpot" />
        </div>
        <div class="game-gallery">
          <Swiper
            :modules="modules"
            :slidesPerView="'auto'"
            :autoplay="false"
            :centeredSlides="false"
            :spaceBetween="16"
            :pagination="false"
            :navigation="true"
            :initialSlide="0"
            @swiper="onJackpotSwiperInit"
          >
            <SwiperSlide
              v-for="(item, index) in topJackpots"
              :key="index"
              @click="playGame(item?.game ?? {})"
            >
              <div class="top-winner-card">
                <img :src="item?.game?.img?.thumbnail.url" :alt="item?.game?.title" />
                <div class="top-winner-card__footer__container">
                  <NuxtImg
                    v-if="index < 3"
                    class="top-winner-card__footer-medal"
                    :alt="`${index + 1} place medal`"
                    format="webp"
                    :src="`/assets/images/mobile/home/medal-${index + 1}.png`"
                  />
                  <div
                    :class="[
                      'top-winner-card__footer',
                      index < 3 ? 'top-winner-card__footer--highlighted' : ''
                    ]"
                  >
                    <NuxtImg
                      v-if="index >= 3"
                      :width="16"
                      :height="12"
                      class="icon-coin"
                      format="webp"
                      src="/assets/images/icons/ic-coin-bag.webp"
                      loading="lazy"
                      alt="coin"
                      densities="1x 2x"
                    />
                    <template v-if="index >= 3">
                      <span><AnimatedNumber :number="item?.value" :start="item?.value" /></span>
                    </template>
                    <template v-else>
                      <AnimatedNumber :number="item?.value" :start="item?.value" />
                    </template>
                  </div>
                </div>
                </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>

      <div class="games-slider">
        <template v-if="topWinners?.length">
          <Swiper
            :modules="modules"
            :slidesPerView="'auto'"
            :autoplay="{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }"
            :centeredSlides="false"
            :spaceBetween="8"
            loop
            :pagination="false"
            :initialSlide="0"
          >
            <SwiperSlide v-for="(item, index) in topWinners" :key="index" @click="playGame(item.recent_game)">
              <div class="top-winner-card">
                <img
                  class="top-winner-card__thumbnail"
                  :src="item?.recent_game?.img?.thumbnail.url"
                  :alt="`${index + 1} game thumbnail`"
                />
                <div class="top-winner-card__content">
                  <div class="badge">
                    <span>THẮNG</span>
                  </div>
                  <div class="game-info">
                    <span class="game-info__title">{{ item.recent_game?.title }}</span>
                    <span class="game-info__user">{{ item.username }}</span>
                    <span class="game-info__amount">{{ formatMoney(item.amount) }} {{ FLAT_CURRENCY_UNIT }}</span>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </template>
        <Skeleton v-else height="110px" />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@import '~scssAsset/desktop/pages/home/jackpot-section.scss';
</style>
